(() => {
  const allowedViews = new Set(["1", "2", "3"]);
  const viewStorageKey = "cwCollectionView";
  const desktopMedia = window.matchMedia("(min-width: 1101px)");
  let desktopFiltersCollapsed = false;
  let cleanup = null;

  const getSavedView = () => {
    try {
      const savedView = window.localStorage.getItem(viewStorageKey);
      return allowedViews.has(savedView) ? savedView : "3";
    } catch (_error) {
      return "3";
    }
  };

  const saveView = (view) => {
    try {
      window.localStorage.setItem(viewStorageKey, view);
    } catch (_error) {
      // The grid remains usable when storage is unavailable.
    }
  };

  const applyView = (view, shouldSave = true) => {
    const nextView = allowedViews.has(view) ? view : "3";

    document.querySelectorAll("[data-collection-grid]").forEach((grid) => {
      grid.dataset.view = nextView;
    });

    document.querySelectorAll("[data-collection-view]").forEach((button) => {
      const isActive = button.dataset.collectionView === nextView;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    if (shouldSave) {
      saveView(nextView);
    }
  };

  const applyDesktopFilterState = (isCollapsed) => {
    desktopFiltersCollapsed = isCollapsed;
    const shouldCollapse = desktopMedia.matches && isCollapsed;

    document.querySelectorAll("[data-collection-layout]").forEach((layout) => {
      layout.classList.toggle("is-filter-collapsed", shouldCollapse);

      const filters = layout.querySelector(".cw-collection__filters");
      if (filters) {
        if (desktopMedia.matches && shouldCollapse) {
          filters.setAttribute("aria-hidden", "true");
          filters.setAttribute("inert", "");
        } else {
          filters.removeAttribute("aria-hidden");
          filters.removeAttribute("inert");
        }
      }

      layout.querySelectorAll("[data-desktop-filter-toggle]").forEach((button) => {
        button.setAttribute("aria-expanded", shouldCollapse ? "false" : "true");

        const showLabel = button.querySelector("[data-filter-show]");
        const hideLabel = button.querySelector("[data-filter-hide]");
        if (showLabel) showLabel.hidden = !shouldCollapse;
        if (hideLabel) hideLabel.hidden = shouldCollapse;
      });
    });

  };

  const positionSortMenu = (menu) => {
    if (!menu.hasAttribute("open")) {
      return;
    }

    const container = menu.closest(".cw-sortbox");
    const button = container?.querySelector(".cw-sortbox__button");
    if (!container || !button) {
      return;
    }

    const gap = 10;
    const containerRect = container.getBoundingClientRect();
    const appBar = document.querySelector(".cw-app-bar");
    const appBarIsVisible = appBar && window.getComputedStyle(appBar).display !== "none";
    const viewportBottom = appBarIsVisible
      ? Math.min(window.innerHeight, appBar.getBoundingClientRect().top)
      : window.innerHeight;
    const availableBelow = Math.max(0, viewportBottom - containerRect.bottom - gap);
    const header = document.querySelector(".shopify-section--header");
    const viewportTop = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    const availableAbove = Math.max(0, containerRect.top - viewportTop - gap);
    const preferredMinimum = Math.min(menu.scrollHeight, 180);
    const shouldOpenAbove = availableBelow < preferredMinimum && availableAbove > availableBelow;
    const availableHeight = shouldOpenAbove ? availableAbove : availableBelow;

    menu.classList.toggle("is-above", shouldOpenAbove);
    menu.style.setProperty("--cw-sort-menu-max-height", `${Math.max(0, Math.floor(availableHeight))}px`);
  };

  const initializeSortMenu = (signal, observers, frames) => {
    const root = document.querySelector("#facet-main");
    if (!root) {
      return;
    }

    const updatePosition = () => {
      if (frames.sort) window.cancelAnimationFrame(frames.sort);
      frames.sort = window.requestAnimationFrame(() => {
        frames.sort = 0;
        const menu = root.querySelector(".cw-sortbox__menu");
        if (menu) positionSortMenu(menu);
      });
    };
    // Beim Öffnen die Seite so weit scrollen, dass alle Optionen über der App-Bar
    // Platz haben, und die aktive Option im Menü sichtbar machen.
    const revealMenu = (menu) => {
      const container = menu.closest(".cw-sortbox");
      if (!container) return;
      const gap = 10;
      const appBar = document.querySelector(".cw-app-bar");
      const appBarIsVisible = appBar && window.getComputedStyle(appBar).display !== "none";
      const viewportBottom = appBarIsVisible
        ? Math.min(window.innerHeight, appBar.getBoundingClientRect().top)
        : window.innerHeight;
      const header = document.querySelector(".shopify-section--header");
      const viewportTop = Math.max(0, header?.getBoundingClientRect().bottom || 0);
      const containerRect = container.getBoundingClientRect();
      const missing = containerRect.bottom + gap + menu.scrollHeight - viewportBottom;
      const scrollable = containerRect.top - viewportTop - gap;
      const delta = Math.min(missing, scrollable);
      if (delta > 0) window.scrollBy({ top: Math.ceil(delta), behavior: "auto" });
      updatePosition();
      window.requestAnimationFrame(() => {
        const checked = menu.querySelector("input:checked")?.closest(".popover__choice-item");
        if (!checked) return;
        const menuRect = menu.getBoundingClientRect();
        const itemRect = checked.getBoundingClientRect();
        if (itemRect.bottom > menuRect.bottom) menu.scrollTop += itemRect.bottom - menuRect.bottom;
      });
    };

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        const target = record.target;
        if (target instanceof Element && target.matches(".cw-sortbox__menu") &&
            record.oldValue === null && target.hasAttribute("open")) {
          revealMenu(target);
        }
      }
      updatePosition();
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["open"],
      attributeOldValue: true,
      subtree: true
    });
    observers.push(observer);

    window.addEventListener("resize", updatePosition, { passive: true, signal });
    window.addEventListener("scroll", updatePosition, { passive: true, signal });
    window.visualViewport?.addEventListener("resize", updatePosition, { passive: true, signal });
  };

  const initialize = () => {
    cleanup?.();

    const controller = new AbortController();
    const { signal } = controller;
    const observers = [];
    const frames = { filter: 0, sort: 0 };

    applyView(getSavedView(), false);
    desktopFiltersCollapsed = false;
    applyDesktopFilterState(desktopFiltersCollapsed);
    frames.filter = window.requestAnimationFrame(() => {
      frames.filter = 0;
      document.querySelectorAll("[data-collection-layout]").forEach((layout) => {
        layout.classList.add("is-desktop-filter-ready");
      });
    });
    initializeSortMenu(signal, observers, frames);

    document.addEventListener("click", (event) => {
      const filterButton = event.target.closest("[data-desktop-filter-toggle]");
      if (filterButton) {
        const layout = filterButton.closest("[data-collection-layout]");
        const isCollapsed = layout?.classList.contains("is-filter-collapsed") ?? true;
        applyDesktopFilterState(!isCollapsed);
        return;
      }

      const viewButton = event.target.closest("[data-collection-view]");
      if (viewButton) {
        applyView(viewButton.dataset.collectionView);
      }
    }, { signal });

    desktopMedia.addEventListener("change", () => {
      applyDesktopFilterState(desktopFiltersCollapsed);
    }, { signal });

    const facetMain = document.querySelector("#facet-main");
    if (facetMain) {
      const facetObserver = new MutationObserver(() => {
        applyView(getSavedView(), false);
        applyDesktopFilterState(desktopFiltersCollapsed);
      });
      facetObserver.observe(facetMain, {
        childList: true,
        subtree: true
      });
      observers.push(facetObserver);
    }

    cleanup = () => {
      controller.abort();
      observers.forEach((observer) => observer.disconnect());
      if (frames.filter) window.cancelAnimationFrame(frames.filter);
      if (frames.sort) window.cancelAnimationFrame(frames.sort);
      cleanup = null;
    };
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.querySelector("#facet-main")) initialize();
  });
  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target.querySelector("#facet-main")) cleanup?.();
  });
})();
